import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { X, Play, Trash2, Plus, Upload as UploadIcon } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Video player modal. Accepts a list of videos [{url, title}].
 * Also lets the user upload more videos.
 */
export default function VideoPlayer({ section, isNight, onClose, onUpdated }) {
    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const { isAdmin } = useAdmin();
    const videos = section?.media?.videos || [];
    const [current, setCurrent] = useState(videos.length > 0 ? 0 : null);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {
        if (current === null && videos.length > 0) setCurrent(0);
        if (current !== null && current >= videos.length) setCurrent(videos.length - 1 || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videos.length]);

    const onPickFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            let updated = section;
            for (const f of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("kind", "video");
                fd.append("title", f.name.replace(/\.[^.]+$/, ""));
                const res = await axios.post(`${API}/sections/${section.id}/media`, fd);
                updated = res.data;
            }
            onUpdated && onUpdated(updated);
            if (current === null) setCurrent(0);
        } catch (e) {
            console.error("Video upload failed", e);
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onDelete = async (idx) => {
        try {
            const res = await axios.delete(`${API}/sections/${section.id}/media`, {
                params: { kind: "video", index: idx },
            });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); }
    };

    return (
        <div
            data-testid={`video-player-${section.id}`}
            className="absolute inset-0 z-50 flex items-center justify-center px-4 py-16"
            style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(14px)" }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
        >
            <div
                className={`${isNight ? "glass" : "glass-day"} rounded-2xl p-5 w-full max-w-5xl max-h-full overflow-y-auto`}
                style={{ border: `1px solid ${accent}55` }}
            >
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[10px] tracking-[0.4em] uppercase" style={{ color: accent }}>
                            {section.label} · Cinema
                        </div>
                        <h2
                            className="font-serif-display text-3xl uppercase tracking-luxe mt-1"
                            style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                        >
                            Video Playlist
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid={`video-player-close-${section.id}`}
                        className="w-10 h-10 rounded-full grid place-items-center"
                        style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}55` }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Active video */}
                <div
                    className="relative w-full aspect-video rounded-xl overflow-hidden bg-black"
                    style={{ border: `1px solid ${accent}33` }}
                >
                    {current !== null && videos[current] ? (
                        <video
                            ref={videoRef}
                            src={videos[current].url}
                            controls
                            autoPlay
                            className="w-full h-full object-contain bg-black"
                            data-testid={`video-element-${section.id}`}
                        />
                    ) : (
                        <div
                            className="w-full h-full grid place-items-center text-center px-6"
                            style={{ color: accent }}
                        >
                            <div>
                                <Play size={32} className="mx-auto mb-3" />
                                <p className="text-[11px] tracking-[0.3em] uppercase">No videos yet</p>
                                <p className="text-xs mt-2 opacity-70 normal-case tracking-normal">
                                    Upload an .mp4, .webm, .mov, or .m4v file to start the show.
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Playlist */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {videos.map((v, idx) => (
                        <div
                            key={`${v.url}-${idx}`}
                            data-testid={`video-item-${section.id}-${idx}`}
                            className={`relative rounded-xl p-3 cursor-pointer transition-all ${current === idx ? "scale-[1.01]" : "hover:scale-[1.01]"}`}
                            style={{
                                background: current === idx
                                    ? (isNight ? `${accent}18` : `${accent}22`)
                                    : (isNight ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"),
                                border: `1px solid ${current === idx ? accent : accent + "33"}`,
                            }}
                            onClick={() => setCurrent(idx)}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-9 h-9 rounded-full grid place-items-center shrink-0"
                                    style={{ background: accent, color: "#0a0a0a" }}
                                >
                                    <Play size={14} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div
                                        className="text-[10px] tracking-[0.2em] uppercase opacity-60"
                                        style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                                    >
                                        Track {idx + 1}
                                    </div>
                                    <div
                                        className="text-sm truncate"
                                        style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                                    >
                                        {v.title || `Video ${idx + 1}`}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onDelete(idx); }}
                                    data-testid={`video-delete-${section.id}-${idx}`}
                                    className="w-7 h-7 rounded-full grid place-items-center shrink-0"
                                    style={{
                                        background: isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                                        color: isNight ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
                                        visibility: isAdmin ? "visible" : "hidden",
                                    }}
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    ))}

                    {/* Upload tile — admin only */}
                    {isAdmin && (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        data-testid={`video-upload-${section.id}`}
                        className="rounded-xl p-3 text-left transition-all hover:scale-[1.01]"
                        style={{
                            background: isNight ? `${accent}10` : `${accent}15`,
                            border: `1px dashed ${accent}80`,
                            color: accent,
                        }}
                    >
                        <div className="flex items-center gap-3">
                            <div
                                className="w-9 h-9 rounded-full grid place-items-center shrink-0"
                                style={{ background: accent, color: "#0a0a0a" }}
                            >
                                {uploading ? <UploadIcon size={14} className="animate-pulse" /> : <Plus size={14} />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] tracking-[0.2em] uppercase opacity-60">Add</div>
                                <div className="text-sm">{uploading ? "Uploading…" : "Upload Video"}</div>
                            </div>
                        </div>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="video/mp4,video/webm,video/quicktime,video/x-m4v,video/ogg"
                            multiple
                            className="hidden"
                            data-testid={`video-file-input-${section.id}`}
                            onChange={(e) => onPickFiles(e.target.files)}
                        />
                    </button>
                    )}
                </div>
            </div>
        </div>
    );
}
